import 'babel-polyfill';

import React from 'react';
import { render } from 'react-dom';
import { Provider } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import { ConnectedRouter, routerMiddleware, routerReducer as router } from 'react-router-redux';
import { createStore, combineReducers, applyMiddleware, compose } from 'redux';
import thunkMiddleware from 'redux-thunk';
import { reducer as form } from 'redux-form';
import createHistory from 'history/createBrowserHistory';
import bugsnag from '@bugsnag/js';
import bugsnagReact from '@bugsnag/plugin-react';

import authReducer from './components/auth/auth-reducer';
import metaReducer from './App/meta-reducer'
import messagesReducer from './components/FlashMessages/messages-reducer';
import entitiesReducer from './App/entities';
import analyticsReducer from './components/Analytics/analytics-reducer';
import modalReducer from './components/Modal/modalReducer';
import App from './App/App';
import FallbackComponent from './components/NotFound/ServerError';
import { theme } from './common/utils/theme';
import { LOGOUT_SUCCESS } from './components/auth/auth-actions';

import './styles/react-bootstrap-table.css';
import './styles/index.css';


export const history = createHistory();
const routeMiddleware = routerMiddleware(history)
let middleware = [thunkMiddleware,
  routeMiddleware];

//Set stage appropriately as Node_env will be set to production
// for any bundle created by 'build' react script
let stage = process.env.REACT_APP_BASE_API_URL.includes('staging') ? 'staging' : 'production';
if (process.env.NODE_ENV !== 'production') {
  const { createLogger } = require(`redux-logger`);
  const loggerMiddleware = createLogger();
  const lm = loggerMiddleware;
  middleware = [...middleware, lm];
  stage = 'development';
}

export const allowUnreleased = process.env.REACT_APP_VIEW_TYPE === 'new';

const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

const appReducer = combineReducers({
  auth: authReducer,
  form,
  router,
  meta: metaReducer,
  messagesReducer,
  reportSettings: analyticsReducer,
  entities: entitiesReducer,
  modalReducer
});

const rootReducer = (state, action) => {
  if (action.type === LOGOUT_SUCCESS) {
    state = undefined;
  }
      
  return appReducer(state, action)
}

function configureStore(preloadedState = {}) {
  return createStore(
    rootReducer,
    preloadedState,
    composeEnhancers(
      applyMiddleware(...middleware)
    )
  );
}
const appStore = configureStore(); 

// Every bugsnag report will be accompanied by user details
const updateUserDetails = (report) => {
  report.user = {
    id: appStore.getState().auth.user.account_id,
    name: `${appStore.getState().auth.user.first_name} ${appStore.getState().auth.user.last_name}`,
    email: appStore.getState().auth.user.email
  };
};
//configure client and reporting boundry
const bugsnagClient = bugsnag({
  apiKey: process.env.REACT_APP_BUGSNAG_KEY || 'null',
  releaseStage: stage,
  beforeSend: updateUserDetails
});
bugsnagClient.use(bugsnagReact, React);
const ErrorBoundary = bugsnagClient.getPlugin('react');

//MessageListeners allow information to be consumed from internal app
const internalDomain = process.env.REACT_APP_INTERNAL_DOMAIN;
const messageListener = (evt) => {
  //handle actions from internal app
  if (evt.origin !== internalDomain) {
    return;
  } else if (evt.data.messageType === 'viewAsUser') {
    //only refresh page if the account Id is being changed by message
    if (
      (evt.data.secureDetails.accountId !==
        window.sessionStorage.accountId) ||
      evt.data.secureDetails.userId !== window.sessionStorage.userId
    ) {
      var refreshNeeded = true;
    }
    //set session storage acct and user ids to be customer viewed
    //authToken and viewing user id are internal user's
    for (let i in evt.data.secureDetails) {
      window.sessionStorage[i] = evt.data.secureDetails[i];
    }
    //need to reload to trigger dashboard load with user data
    // (and navigate to dashboard if not previously logged in)
    refreshNeeded && window.location.replace('/dashboard');
  }
}
window.addEventListener('message', messageListener);
if (window.opener){
  window.opener.postMessage({ messageType: 'loadState', value: 'true' }, internalDomain)
}

render(
  <ErrorBoundary FallbackComponent={FallbackComponent}>
    <Provider store={appStore}>
      <ConnectedRouter history={history}>
        <ThemeProvider theme={theme}>
          <App bugs={bugsnagClient} />
        </ThemeProvider>
      </ConnectedRouter>
    </Provider>
  </ErrorBoundary>,
  document.getElementById('root')
);

export { bugsnagClient, appStore }
